import React, { useContext, useMemo } from "react";
import { message, Card } from "antd";
import axios from "axios";
import { useHistory } from "react-router-dom";

import config from "../../config";
import { LoadingContext } from "../../providers/LoadingContext";
import { capitalizeFirstLetter } from "../../helpers";

// ---------- localStorage safe helper ----------
const safeJSONParse = (value, fallback = null) => {
  try {
    if (!value || value === "undefined" || value === "null") return fallback;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const AccountChange = () => {
  const { setGlobalLoading } = useContext(LoadingContext);
  const history = useHistory();

  // ✅ safe parse (prevents blank screen if localStorage has bad JSON)
  const user = useMemo(
    () => safeJSONParse(localStorage.getItem("user"), null),
    []
  );

  const requireAuth = () => {
    if (!user || !localStorage.getItem("access_token")) {
      message.info("Please login to use this feature.");
      history.push("/login");
      return false;
    }
    return true;
  };

  const onCardClick = (role) => {
    if (!requireAuth()) return;

    if (role === "candidate") onRequestCandidate();
    if (role === "advertiser") onRequestAdvertiser();
  };

  const onRequestCandidate = async () => {
    setGlobalLoading(true);
    try {
      const queueData = JSON.stringify({
        userId: localStorage.getItem("userid") || user?._id || "",
        role: user?.role || "user",
        category: "Change Account Type",
        task_name: `${
          user?.nickname || user?.name || "User"
        } has request to change account type from User to Candidate (${user?.email || ""})`,
        user,
        status: "in queue",
        userToken: localStorage.getItem("access_token"),
      });

      await axios.post(
        `${config.QUEUES}/queues`,
        {},
        {
          headers: {
            token: localStorage.getItem("access_token"),
            doc: encodeURIComponent(queueData),
          },
        }
      );

      message.success("User Account Change has been requested!");
      history.push("/");
    } catch (err) {
      console.log(err);
      message.error(
        "Something went wrong requesting account change. Please try again."
      );
    } finally {
      setGlobalLoading(false);
    }
  };

  const onRequestAdvertiser = async () => {
    setGlobalLoading(true);

    try {
      const doc = JSON.stringify({ role: "advertiser" });

      const { data } = await axios.patch(
        `${config.USERS}/users`,
        {},
        {
          headers: {
            token: localStorage.getItem("access_token"),
            doc: encodeURIComponent(doc),
          },
        }
      );

      // ✅ handle different possible response shapes safely
      const updatedUser =
        data?.value || data?.user || data || safeJSONParse(localStorage.getItem("user"), {}) || {};

      localStorage.setItem("user", JSON.stringify(updatedUser));
      localStorage.setItem("role", "advertiser");

      message.success("Account Type changed to advertiser");
      history.push("/signup/advertiser");
    } catch (err) {
      console.log(err);
      message.error(
        "Something went wrong changing account type. Please try again."
      );
    } finally {
      setGlobalLoading(false);
    }
  };

  const accountTypes = [
    {
      title: "advertiser",
      description:
        "Reach influential communities talking about today’s most relevant political topics.",
      icon: "https://s3.amazonaws.com/test.satingroves.com/nocap-test/megaphone.png",
    },
    {
      title: "candidate",
      description:
        "Create a profile to help voters to understand who you are and why you are running for office.",
      icon: "https://s3.amazonaws.com/test.satingroves.com/nocap-test/ambassador.png",
    },
  ];

  return (
    <div style={{ textAlign: "center" }}>
      <h2>Request Account Type to Change</h2>

      <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
        {accountTypes.map((item) => (
          <Card
            key={item.title} // ✅ important React key
            style={{ width: 230, height: 315, cursor: "pointer" }}
            className="black-border-hover"
            onClick={() => onCardClick(item.title)}
          >
            <img
              src={item.icon}
              alt={item.title}
              style={{
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
                width: "50%",
                paddingBottom: "1em",
              }}
            />

            <h2 style={{ textAlign: "center" }}>
              {capitalizeFirstLetter(item.title)}
            </h2>

            <div>{item.description}</div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AccountChange;
